import React, { useState, useEffect, useCallback } from "react";
import { auleloApi as api } from "@/lib/auleloApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, UserPlus, Trash2, Loader2, Sparkles, RefreshCw, ExternalLink, Newspaper, Twitter, Linkedin, Instagram, Facebook, KeyRound, Eye, EyeOff,
} from "lucide-react";

const PLATFORM_ICON = { x: Twitter, linkedin: Linkedin, instagram: Instagram, facebook: Facebook };

export default function AuleloWatchlist() {
  const [tab, setTab] = useState("experts");
  return (
    <div className="space-y-6" data-testid="aulelo-watchlist-page">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400">// watchlists</div>
          <h1 className="font-mono text-4xl md:text-5xl font-black tracking-tight mt-2 text-emerald-50">Who & what to watch.</h1>
          <p className="mt-3 text-zinc-400 font-mono text-sm max-w-2xl">Track experts, monitor social handles, and vault credentials for paid newspapers. Pulls flow into your dashboard automatically.</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none bg-surface border border-hairline p-0 h-auto">
            <TabsTrigger value="experts" data-testid="watch-tab-experts" className="rounded-none label-mono data-[state=active]:bg-bone data-[state=active]:text-ink px-4 py-2">
              <Users className="w-3.5 h-3.5 mr-2" />Experts
            </TabsTrigger>
            <TabsTrigger value="social" data-testid="watch-tab-social" className="rounded-none label-mono data-[state=active]:bg-bone data-[state=active]:text-ink px-4 py-2">
              <Twitter className="w-3.5 h-3.5 mr-2" />Social handles
            </TabsTrigger>
            <TabsTrigger value="publishers" data-testid="watch-tab-publishers" className="rounded-none label-mono data-[state=active]:bg-bone data-[state=active]:text-ink px-4 py-2">
              <Newspaper className="w-3.5 h-3.5 mr-2" />Newspapers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="experts" className="mt-6"><ExpertsTab /></TabsContent>
          <TabsContent value="social" className="mt-6"><SocialTab /></TabsContent>
          <TabsContent value="publishers" className="mt-6"><PublishersTab /></TabsContent>
        </Tabs>
    </div>
  );
}

// ===== Experts =====
function ExpertsTab() {
  const [list, setList] = useState([]);
  const [feed, setFeed] = useState({ items: [] });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedBusy, setFeedBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/watchlist/experts"); setList(data || []); } catch { /* */ }
  }, []);

  const pullFeed = async () => {
    setFeedBusy(true);
    try {
      const { data } = await api.get("/watchlist/feed?limit_per_expert=3");
      setFeed(data);
    } catch (e) { toast.error("Feed fetch failed"); } finally { setFeedBusy(false); }
  };

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Remove expert?")) return;
    try { await api.delete(`/watchlist/experts/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="label-mono text-bone/60">{list.length} expert{list.length === 1 ? "" : "s"} on watch</div>
        <div className="flex gap-2">
          <Button onClick={pullFeed} disabled={feedBusy || list.length === 0} variant="outline" data-testid="experts-pull-feed-btn"
            className="rounded-none border-hairline bg-transparent label-mono hover:bg-bone hover:text-ink h-10">
            {feedBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Pull recent mentions
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="experts-add-btn"
            className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-10">
            <UserPlus className="w-4 h-4 mr-2" /> Add expert
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((e) => (
          <article key={e.id} className="border border-hairline bg-surface p-4 group" data-testid={`expert-${e.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display text-lg font-bold tracking-tightest">{e.name}</h3>
                <div className="label-mono text-[10px] text-bone/50 mt-1">{[e.org, e.role].filter(Boolean).join(" · ") || "—"}</div>
                {e.handle && <div className="label-mono text-[10px] text-klein mt-1">@{e.handle}</div>}
              </div>
              <button onClick={() => del(e.id)} data-testid={`expert-del-${e.id}`} className="opacity-0 group-hover:opacity-100 p-1.5 border border-hairline text-bone/50 hover:text-safety hover:border-safety/40">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            {e.keywords?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {e.keywords.map((k) => (
                  <span key={k} className="label-mono text-[9px] px-1.5 py-0.5 border border-hairline text-bone/60">{k}</span>
                ))}
              </div>
            )}
            {e.notes && <p className="text-bone/65 text-sm mt-3 line-clamp-3">{e.notes}</p>}
          </article>
        ))}
        {list.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 border border-dashed border-hairline p-10 text-center text-bone/50 label-mono text-xs">No experts yet — click <b className="text-bone">Add expert</b>.</div>
        )}
      </div>

      {feed.items?.length > 0 && (
        <div className="mt-8 border-t border-hairline pt-6">
          <div className="label-mono text-bone/60 mb-3">// recent mentions ({feed.items.length})</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {feed.items.map((it, i) => (
              <a key={i} href={it.url} target="_blank" rel="noreferrer" data-testid={`feed-item-${i}`}
                className="border border-hairline bg-ink p-4 hover:border-klein transition block">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="label-mono text-[9px] text-klein">{it.expert_name}</div>
                  <ExternalLink className="w-3 h-3 text-bone/40" />
                </div>
                <div className="font-display text-base font-bold tracking-tightest leading-snug line-clamp-3">{it.title}</div>
                <p className="text-bone/60 text-sm mt-2 line-clamp-3">{it.snippet}</p>
                <div className="label-mono text-[9px] text-bone/40 mt-2">{it.source}</div>
              </a>
            ))}
          </div>
        </div>
      )}

      <ExpertAddDialog open={open} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />
    </div>
  );
}

function ExpertAddDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", handle: "", org: "", role: "", keywords: "", notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", handle: "", org: "", role: "", keywords: "", notes: "" }); }, [open]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post("/watchlist/experts", {
        ...form,
        keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Added");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="expert-add-dialog" className="rounded-none bg-ink border-hairline text-bone max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tightest text-bone">Add expert</DialogTitle></DialogHeader>
        {["name", "handle", "org", "role"].map((k) => (
          <div key={k}>
            <Label className="label-mono text-bone/70">{k}{k === "handle" && " (no @)"}</Label>
            <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10" data-testid={`expert-${k}`} />
          </div>
        ))}
        <div>
          <Label className="label-mono text-bone/70">Keywords (comma-separated)</Label>
          <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="geopolitics, china, ai" data-testid="expert-keywords" className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10" />
        </div>
        <div>
          <Label className="label-mono text-bone/70">Notes</Label>
          <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein" data-testid="expert-notes" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none border-hairline bg-transparent label-mono">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono" data-testid="expert-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== Social handles =====
function SocialTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/social/handles"); setList(data || []); } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const analyse = async (h) => {
    setAnalysing(h.id);
    setAnalysis(null);
    try {
      const { data } = await api.post(`/social/handles/${h.id}/analyse`);
      setAnalysis(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Analyse failed"); }
    finally { setAnalysing(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove handle?")) return;
    try { await api.delete(`/social/handles/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="label-mono text-bone/60">{list.length} handle{list.length === 1 ? "" : "s"} configured</div>
        <Button onClick={() => setOpen(true)} data-testid="social-add-btn" className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-10">
          <UserPlus className="w-4 h-4 mr-2" /> Add handle
        </Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((h) => {
          const Icon = PLATFORM_ICON[h.platform] || Twitter;
          return (
            <article key={h.id} className="border border-hairline bg-surface p-4 group" data-testid={`handle-${h.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-hairline bg-ink flex items-center justify-center"><Icon className="w-4 h-4 text-klein" /></div>
                  <div>
                    <h3 className="font-display text-base font-bold tracking-tightest">@{h.handle}</h3>
                    <div className="label-mono text-[10px] text-bone/50 uppercase">{h.platform}</div>
                  </div>
                </div>
                <button onClick={() => del(h.id)} data-testid={`handle-del-${h.id}`} className="opacity-0 group-hover:opacity-100 p-1.5 border border-hairline text-bone/50 hover:text-safety">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <Button onClick={() => analyse(h)} disabled={analysing === h.id} variant="outline" data-testid={`handle-analyse-${h.id}`}
                className="mt-3 w-full rounded-none border-hairline bg-transparent label-mono hover:bg-bone hover:text-ink h-9">
                {analysing === h.id ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Sparkles className="w-3 h-3 mr-2" />}
                Analyse
              </Button>
              {h.last_analysed_at && <div className="label-mono text-[9px] text-bone/40 mt-2">last: {h.last_analysed_at.slice(0, 16).replace("T", " · ")}</div>}
            </article>
          );
        })}
        {list.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 border border-dashed border-hairline p-10 text-center text-bone/50 label-mono text-xs">No handles yet.</div>
        )}
      </div>

      {analysis && (
        <Dialog open={!!analysis} onOpenChange={(v) => !v && setAnalysis(null)}>
          <DialogContent data-testid="handle-analysis-dialog" className="rounded-none bg-ink border-hairline text-bone max-w-2xl max-h-[80vh] overflow-y-auto scrollbar-thin">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tightest text-bone">@{analysis.handle.handle} · {analysis.handle.platform}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="border border-hairline bg-surface p-4">
                <div className="label-mono text-bone/60 mb-2">// summary</div>
                <p className="text-bone/90 leading-relaxed whitespace-pre-wrap">{analysis.summary}</p>
              </div>
              <div>
                <div className="label-mono text-bone/60 mb-2">// raw items ({analysis.items?.length || 0})</div>
                <div className="space-y-2">
                  {(analysis.items || []).map((it, i) => (
                    <a key={i} href={it.url} target="_blank" rel="noreferrer" className="block border border-hairline bg-surface p-3 hover:border-klein">
                      <div className="font-display text-sm font-bold tracking-tightest line-clamp-2">{it.title}</div>
                      <div className="label-mono text-[9px] text-bone/40 mt-1">{it.source}</div>
                      <p className="text-bone/60 text-xs mt-1 line-clamp-2">{it.snippet}</p>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <SocialAddDialog open={open} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />
    </div>
  );
}

function SocialAddDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ platform: "x", handle: "", display_name: "" });
  useEffect(() => { if (open) setForm({ platform: "x", handle: "", display_name: "" }); }, [open]);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.handle.trim()) { toast.error("Handle required"); return; }
    setSaving(true);
    try {
      await api.post("/social/handles", form);
      toast.success("Added");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="social-add-dialog" className="rounded-none bg-ink border-hairline text-bone max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tightest text-bone">Add social handle</DialogTitle></DialogHeader>
        <div>
          <Label className="label-mono text-bone/70">Platform</Label>
          <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
            <SelectTrigger className="mt-1 rounded-none bg-surface border-hairline" data-testid="social-platform"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none bg-surface border-hairline">
              <SelectItem value="x" className="rounded-none">X / Twitter</SelectItem>
              <SelectItem value="linkedin" className="rounded-none">LinkedIn</SelectItem>
              <SelectItem value="instagram" className="rounded-none">Instagram</SelectItem>
              <SelectItem value="facebook" className="rounded-none">Facebook</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-mono text-bone/70">Handle (no @)</Label>
          <Input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10" data-testid="social-handle" />
        </div>
        <div>
          <Label className="label-mono text-bone/70">Display name (optional)</Label>
          <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none border-hairline bg-transparent label-mono">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono" data-testid="social-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== Publishers =====
function PublishersTab() {
  const [supported, setSupported] = useState([]);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [fetching, setFetching] = useState(null);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([api.get("/publishers/supported"), api.get("/publishers")]);
      setSupported(s.data || []);
      setList(l.data || []);
    } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const fetchPub = async (p) => {
    setFetching(p.id);
    try {
      const { data } = await api.post(`/publishers/${p.id}/fetch`);
      setResults(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fetch failed"); }
    finally { setFetching(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove credentials? They will be permanently deleted.")) return;
    try { await api.delete(`/publishers/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="border border-yellow-500/40 bg-yellow-500/5 p-4 text-sm text-yellow-100/85">
        <div className="label-mono text-yellow-300 mb-1">// honest caveat</div>
        Credentials are <b>vaulted encrypted at rest</b>. However, programmatically logging into paywalled sites (Atlantic, Bloomberg, Diplomat, Economist, FT) is brittle — each site uses its own session, 2FA, and bot detection. <b>This first version returns recent public headlines via web-search</b>; full authenticated fetch requires bespoke per-publisher code we can add as you confirm each site's flow.
      </div>

      <div className="flex items-center justify-between">
        <div className="label-mono text-bone/60">{list.length} of {supported.length} publishers configured</div>
        <Button onClick={() => setOpen(true)} data-testid="publisher-add-btn" className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-10">
          <KeyRound className="w-4 h-4 mr-2" /> Add credentials
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((p) => (
          <article key={p.id} className="border border-hairline bg-surface p-4 group" data-testid={`publisher-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border border-hairline bg-ink flex items-center justify-center"><Newspaper className="w-4 h-4 text-klein" /></div>
                <div>
                  <h3 className="font-display text-base font-bold tracking-tightest">{p.meta?.name || p.slug}</h3>
                  <div className="label-mono text-[10px] text-bone/50 mt-1 line-clamp-1">{p.username}</div>
                </div>
              </div>
              <button onClick={() => del(p.id)} className="opacity-0 group-hover:opacity-100 p-1.5 border border-hairline text-bone/50 hover:text-safety">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            <Button onClick={() => fetchPub(p)} disabled={fetching === p.id} variant="outline" data-testid={`publisher-fetch-${p.id}`}
              className="mt-3 w-full rounded-none border-hairline bg-transparent label-mono hover:bg-bone hover:text-ink h-9">
              {fetching === p.id ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-2" />}
              Fetch headlines
            </Button>
            {p.last_fetched_at && <div className="label-mono text-[9px] text-bone/40 mt-2">last: {p.last_fetched_at.slice(0, 16).replace("T", " · ")}</div>}
          </article>
        ))}
        {list.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 border border-dashed border-hairline p-10 text-center text-bone/50 label-mono text-xs">No credentials saved.</div>
        )}
      </div>

      {results && (
        <Dialog open={!!results} onOpenChange={(v) => !v && setResults(null)}>
          <DialogContent data-testid="publisher-results-dialog" className="rounded-none bg-ink border-hairline text-bone max-w-2xl max-h-[80vh] overflow-y-auto scrollbar-thin">
            <DialogHeader><DialogTitle className="font-display tracking-tightest text-bone">{results.publisher?.meta?.name} · {results.tier}</DialogTitle></DialogHeader>
            <div className="border border-yellow-500/30 bg-yellow-500/5 p-3 text-xs text-yellow-100/80">{results.note}</div>
            <div className="space-y-2">
              {(results.items || []).map((it, i) => (
                <a key={i} href={it.url} target="_blank" rel="noreferrer" className="block border border-hairline bg-surface p-3 hover:border-klein">
                  <div className="font-display text-sm font-bold tracking-tightest line-clamp-2">{it.title}</div>
                  <p className="text-bone/60 text-xs mt-1 line-clamp-2">{it.snippet}</p>
                </a>
              ))}
              {(results.items || []).length === 0 && <div className="text-bone/50 text-sm">No items returned.</div>}
            </div>
          </DialogContent>
        </Dialog>
      )}

      <PublisherAddDialog open={open} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} supported={supported} existing={list} />
    </div>
  );
}

function PublisherAddDialog({ open, onClose, onCreated, supported, existing }) {
  const [form, setForm] = useState({ slug: "ft", username: "", password: "" });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // pick the first not-yet-configured publisher as default
      const taken = new Set(existing.map((e) => e.slug));
      const first = supported.find((s) => !taken.has(s.slug))?.slug || "ft";
      setForm({ slug: first, username: "", password: "" });
    }
  }, [open, supported, existing]);

  const submit = async () => {
    if (!form.username.trim() || !form.password) { toast.error("Username and password required"); return; }
    setSaving(true);
    try {
      await api.post("/publishers", form);
      toast.success("Credentials saved (encrypted)");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="publisher-add-dialog" className="rounded-none bg-ink border-hairline text-bone max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tightest text-bone">Add publisher credentials</DialogTitle></DialogHeader>
        <div>
          <Label className="label-mono text-bone/70">Publisher</Label>
          <Select value={form.slug} onValueChange={(v) => setForm({ ...form, slug: v })}>
            <SelectTrigger className="mt-1 rounded-none bg-surface border-hairline" data-testid="publisher-slug"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none bg-surface border-hairline">
              {supported.map((s) => (<SelectItem key={s.slug} value={s.slug} className="rounded-none">{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-mono text-bone/70">Username / email</Label>
          <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10" data-testid="publisher-username" />
        </div>
        <div>
          <Label className="label-mono text-bone/70">Password</Label>
          <div className="flex gap-1 mt-1">
            <Input type={show ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10" data-testid="publisher-password" />
            <button type="button" onClick={() => setShow(!show)} className="border border-hairline bg-surface px-3 text-bone/60">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          </div>
        </div>
        <div className="label-mono text-[10px] text-bone/50">Encrypted at rest with a key derived from your server JWT secret. Never returned to the UI.</div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none border-hairline bg-transparent label-mono">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono" data-testid="publisher-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
